var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * Two DIFFERENT authority records side by side, field by field, in MARC form — the rows come
 * from {@link compareRecords} (CZNA-1466), which pairs fields by content, not by position.
 *
 * <p>CZNA-1577 — drawn first inside the duplicate window of the proposal form (CZNA-1466),
 * now also on the „Zrušit s náhradou“ page, where a supervisor decides whether two records
 * describe the same entity by their content (dates, 670, 4XX), not just by their headings.
 * The two callers name the sides differently — the record being written vs. a candidate, the
 * cancelled record vs. its replacement — so the words (column headings, status labels) are
 * the caller's; the pairing, the marking, the legend and the count are drawn here, once.
 *
 * <p>Read-only by design: no control copies a field to the other side (TASK-109, "Pravidlo
 * pro UI v1" — enriching the kept record is a separate manual edit).
 */
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { unmatchedSubfields } from '../../utils/compareRecords';
var MONO_FONT = "'SF Mono', 'Cascadia Code', 'Consolas', monospace";
// `indicator` paints the row's edge and legend swatch; `text` is the status label,
// dark enough for WCAG AA on the row background at 12px (amber-800, blue-700,
// purple-700 — each above 4.5:1).
var STATUS_COLORS = {
    same: { bg: 'transparent', indicator: 'transparent' },
    ignored: { bg: 'transparent', indicator: 'transparent' },
    different: { bg: '#fffbeb', indicator: '#d97706', text: '#92400e' },
    leftOnly: { bg: '#eff6ff', indicator: '#2563eb', text: '#1d4ed8' },
    rightOnly: { bg: '#faf5ff', indicator: '#9333ea', text: '#7e22ce' },
};
var MARKED_STATUSES = ['different', 'leftOnly', 'rightOnly'];
/** How many rows the grid marks — what its difference count says. */
var countDifferences = function (rows) {
    return rows.filter(function (r) { return MARKED_STATUSES.includes(r.status); }).length;
};
// Status in words first, then the two records: every row says what it is, so the
// colour is a second channel and never the only one.
var compareGridStyle = {
    display: 'grid',
    gridTemplateColumns: '8rem 1fr 1fr',
    columnGap: '16px',
};
var formatIndicator = function (ind) { return (!ind || ind.trim() === '' ? '#' : ind); };
var FieldCell = function (_a) {
    var field = _a.field, highlighted = _a.highlighted, status = _a.status, side = _a.side;
    if (!field)
        return React.createElement("div", { "data-testid": "compare-cell-".concat(side) });
    var control = field.fieldTag < '010';
    var marked = status === 'different';
    return (React.createElement("div", { "data-testid": "compare-cell-".concat(side), style: { fontFamily: MONO_FONT, fontSize: '0.8rem', padding: '2px 0', overflowWrap: 'anywhere' } },
        React.createElement("strong", null, field.fieldTag),
        !control && " ".concat(formatIndicator(field.indicator1)).concat(formatIndicator(field.indicator2)),
        field.subfields.map(function (sf, idx) { return (React.createElement("span", { key: "".concat(sf.subfieldCode, "-").concat(idx), "data-compare-subfield-differs": marked && highlighted.has(sf) ? '' : undefined, style: marked && highlighted.has(sf)
                ? { backgroundColor: '#fde68a', borderRadius: '3px', padding: '0 2px' }
                : undefined },
            ' ',
            !control && React.createElement("span", { style: { color: '#64748b' } },
                "$",
                sf.subfieldCode),
            !control && ' ',
            sf.value)); })));
};
var CompareRow = function (_a) {
    var row = _a.row, statusMessageIds = _a.statusMessageIds;
    var colors = STATUS_COLORS[row.status];
    var marked = MARKED_STATUSES.includes(row.status);
    return (React.createElement("div", { "data-testid": "compare-row-".concat(row.tag, "-").concat(row.status), "data-compare-status": row.status, style: __assign(__assign({}, compareGridStyle), { backgroundColor: colors.bg, borderLeft: "3px solid ".concat(colors.indicator), padding: '2px 6px', margin: '2px 0', color: row.status === 'ignored' ? '#64748b' : undefined }) },
        React.createElement("span", { "data-testid": "compare-status", style: { fontSize: '0.75rem', padding: '3px 0', fontWeight: marked ? 600 : 400, color: colors.text } },
            React.createElement(FormattedMessage, { id: statusMessageIds[row.status] })),
        React.createElement(FieldCell, { side: "left", field: row.left, status: row.status, highlighted: row.left ? unmatchedSubfields(row.left, row.right) : new Set() }),
        React.createElement(FieldCell, { side: "right", field: row.right, status: row.status, highlighted: row.right ? unmatchedSubfields(row.right, row.left) : new Set() })));
};
var LegendSwatch = function (_a) {
    var status = _a.status, messageId = _a.messageId;
    return (React.createElement("span", { style: { display: 'inline-flex', alignItems: 'center', gap: '4px' } },
        React.createElement("span", { style: {
                width: '12px',
                height: '12px',
                backgroundColor: STATUS_COLORS[status].indicator,
                borderRadius: '2px',
                display: 'inline-block',
            } }),
        React.createElement(FormattedMessage, { id: messageId })));
};
var RecordCompareGrid = function (_a) {
    var rows = _a.rows, leftHeading = _a.leftHeading, rightHeading = _a.rightHeading, statusMessageIds = _a.statusMessageIds, differenceCountMessageId = _a.differenceCountMessageId, testIdPrefix = _a.testIdPrefix, children = _a.children;
    return (React.createElement("div", { "data-testid": testIdPrefix },
        React.createElement("div", { style: __assign(__assign({}, compareGridStyle), { fontWeight: 600, marginBottom: '8px' }) },
            React.createElement("div", null),
            React.createElement("div", { "data-testid": "".concat(testIdPrefix, "-left-heading") }, leftHeading),
            React.createElement("div", { "data-testid": "".concat(testIdPrefix, "-right-heading") }, rightHeading)),
        children,
        rows && (React.createElement(React.Fragment, null,
            React.createElement("div", { "data-testid": "".concat(testIdPrefix, "-legend"), style: { display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '8px', fontSize: '0.8rem' } },
                React.createElement("span", { "data-testid": "".concat(testIdPrefix, "-difference-count") },
                    React.createElement(FormattedMessage, { id: differenceCountMessageId, values: { count: countDifferences(rows) } })),
                MARKED_STATUSES.map(function (s) { return React.createElement(LegendSwatch, { key: s, status: s, messageId: statusMessageIds[s] }); })),
            React.createElement("div", null, rows.map(function (row, idx) { return (React.createElement(CompareRow, { key: "".concat(row.tag, "-").concat(idx), row: row, statusMessageIds: statusMessageIds })); }))))));
};
export default RecordCompareGrid;
